var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useMemo } from 'react';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import Loader from '@components/v2/Loader';
import Banner, { BannerVariant } from '@components/Banner';
import ComponentTypesTable from './ComponentTypesTable';
import AddComponentTypeModal from './components/AddComponentTypeModal';
import ComponentTypeDetailModal from './components/ComponentTypeDetailModal';
import ComponentTypesFilterPanel from './components/ComponentTypesFilterPanel';
import RecommendedComponentTypesImportModal from './components/RecommendedComponentTypesImportModal';
import { useComponentTypes, useUpdateComponentTypeMutation, useToggleComponentTypeStatusMutation, useAllowedAttachableTypes } from './hooks/useComponentTypesMutations';
import useFilteredComponentTypes from './hooks/useFilteredComponentTypes';
import './ComponentTypesSettingsStyles.scss';
const namespace = 'features.settings.component_types';
export default function ComponentTypesSettings({ supportsSubcomponents, hasJointCommission = false, hasServiceTradeIntegration = false, canEditComponentTypes = false, canAddComponentTypes = true, canImportComponentTypes = true, hasDnvReporting = false, dnvTypeOptions = [], translationLanguages = [] }) {
    const [selectedType, setSelectedType] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isImportFormOpen, setIsImportFormOpen] = useState(false);
    const { data: componentTypes = [], isLoading, isError, error } = useComponentTypes();
    const { data: allowedAttachableTypes = [] } = useAllowedAttachableTypes();
    const updateMutation = useUpdateComponentTypeMutation();
    const toggleStatusMutation = useToggleComponentTypeStatusMutation();
    // Filtering hooks
    const { filters, setFilters, filteredComponentTypes, onResetCache } = useFilteredComponentTypes({
        componentTypes
    });
    const unconfiguredCount = useMemo(() => componentTypes.filter((ct) => ct.active && (!ct.allowedAttachableTypes || ct.allowedAttachableTypes.length === 0))
        .length, [componentTypes]);
    const handleNewTypeClick = () => {
        setIsAddModalOpen(true);
    };
    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
    };
    const handleEditClick = (componentType) => {
        setSelectedType(componentType);
        setIsDetailModalOpen(true);
    };
    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedType(null);
    };
    const handleUpdate = (typeId, field, value) => __awaiter(this, void 0, void 0, function* () {
        // Special case: batch EP code updates to avoid sending nested object
        if (field === 'epCodeUpdate') {
            const { epCode, ecType } = value;
            yield updateMutation.mutateAsync({
                id: typeId,
                data: {
                    epCode,
                    ecType: ecType
                }
            });
            return;
        }
        yield updateMutation.mutateAsync({
            id: typeId,
            data: { [field]: value }
        });
    });
    const handleToggleActive = (typeId) => __awaiter(this, void 0, void 0, function* () {
        yield toggleStatusMutation.mutateAsync(typeId);
    });
    const handleImportFormClose = () => {
        setIsImportFormOpen(false);
    };
    const handleImportFormOpen = () => {
        setIsImportFormOpen(true);
    };
    if (isLoading) {
        return (React.createElement("div", { className: "component-types-settings" },
            React.createElement("div", { className: "content" },
                React.createElement("div", { className: "loading-state" },
                    React.createElement(Loader, { label: translate('loading', { namespace: 'features.commons' }) })))));
    }
    if (isError) {
        return (React.createElement("div", { className: "component-types-settings" },
            React.createElement("div", { className: "content" },
                React.createElement("div", { className: "error-state" },
                    React.createElement("i", { className: "fa-light fa-exclamation-triangle", "aria-hidden": "true" }),
                    React.createElement("p", null, translate('error_loading', { namespace })),
                    React.createElement("p", null, error === null || error === void 0 ? void 0 : error.message)))));
    }
    return (React.createElement("div", { className: "component-types-settings" },
        React.createElement("div", { className: "content" },
            React.createElement("header", { className: "content-header" },
                React.createElement("div", { className: "content-header__left" },
                    React.createElement("h1", { className: "content-header__title" }, translate('title', { namespace }))),
                React.createElement("div", { className: "content-header__right" },
                    canImportComponentTypes && (React.createElement(Button, { variant: "secondary", onClick: handleImportFormOpen, className: "mr-2" },
                        React.createElement("i", { className: "fa-light fa-lightbulb", "aria-hidden": "true" }),
                        translate('recommended_button', { namespace }))),
                    canAddComponentTypes && (React.createElement(Button, { variant: "primary", onClick: handleNewTypeClick },
                        React.createElement("i", { className: "fa-light fa-plus", "aria-hidden": "true" }),
                        translate('new_type', { namespace }))))),
            React.createElement("hr", { className: "content-separator" }),
            React.createElement("p", { className: "subtitle" }, translate('subtitle', { namespace })),
            unconfiguredCount > 0 && (React.createElement(Banner, { variant: BannerVariant.Warning, text: translate('unconfigured_types_banner', { namespace, count: unconfiguredCount }) })),
            React.createElement(ComponentTypesFilterPanel, { filters: filters, onFiltersChange: setFilters, allowedAttachableTypes: allowedAttachableTypes, onResetCache: onResetCache }),
            React.createElement("div", { className: "content-table" },
                React.createElement("p", { className: "content-description" }, translate('description', { namespace })),
                filteredComponentTypes.length === 0 && componentTypes.length > 0 ? (React.createElement("div", { className: "component-types-settings__empty" },
                    React.createElement("div", { className: "empty-state" },
                        React.createElement("h3", null, translate('empty_state.title', { namespace })),
                        React.createElement("p", null, translate('empty_state.description', { namespace }))))) : (React.createElement(ComponentTypesTable, { componentTypes: filteredComponentTypes, onEditClick: handleEditClick, canEdit: canEditComponentTypes })))),
        React.createElement(AddComponentTypeModal, { isOpen: isAddModalOpen, onClose: handleCloseAddModal, availableAttachableTypes: allowedAttachableTypes, hasJointCommission: hasJointCommission, hasDnvReporting: hasDnvReporting, dnvTypeOptions: dnvTypeOptions, translationLanguages: translationLanguages }),
        React.createElement(ComponentTypeDetailModal, { isOpen: isDetailModalOpen, onClose: handleCloseDetailModal, componentType: componentTypes.find((type) => type.id === (selectedType === null || selectedType === void 0 ? void 0 : selectedType.id)) || null, allComponentTypes: componentTypes, allowedAttachableTypes: allowedAttachableTypes, onUpdate: handleUpdate, onToggleActive: handleToggleActive, isDeleting: toggleStatusMutation.isLoading, supportsSubcomponents: supportsSubcomponents, hasJointCommission: hasJointCommission, hasServiceTradeIntegration: hasServiceTradeIntegration, hasDnvReporting: hasDnvReporting, dnvTypeOptions: dnvTypeOptions, translationLanguages: translationLanguages }),
        React.createElement(RecommendedComponentTypesImportModal, { isOpen: isImportFormOpen, onClose: handleImportFormClose, attachableTypes: allowedAttachableTypes })));
}
